import { AnimatePresence, motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Play, X, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LANES = 3;
const W = 300;
const H = 480;
const LANE_W = W / LANES;
const CAR_W = 40;
const CAR_H = 66;

/**
 * GhostReplayModal: replays a randomly-fetched ghost run in an accelerated
 * "highlight reel" on a small canvas so the player can watch someone else's
 * journey to heaven.
 */
export default function GhostReplayModal({ open, onClose }) {
  const canvasRef = useRef(null);
  const rafRef = useRef(null);
  const [ghost, setGhost] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ready | playing | done | error
  const startRef = useRef(0);
  const speedRef = useRef(2.4); // playback speed multiplier

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setStatus("loading");
    setGhost(null);
    (async () => {
      try {
        const res = await axios.get(`${API}/game-ghosts/random?min_distance=800`);
        if (cancelled) return;
        if (res.data && Array.isArray(res.data.frames) && res.data.frames.length > 1) {
          setGhost(res.data);
          setStatus("ready");
        } else {
          setStatus("error");
        }
      } catch {
        if (!cancelled) setStatus("error");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let roadOffset = 0;

    const draw = (elapsed) => {
      // background gradient tied to progress
      const t = Math.min(1, elapsed / (durationMs() || 1));
      const grad = ctx.createLinearGradient(0, 0, 0, H);
      grad.addColorStop(0, `rgb(${lerp(5, 30, t)},${lerp(5, 20, t)},${lerp(10, 8, t)})`);
      grad.addColorStop(1, `rgb(${lerp(15, 90, t)},${lerp(17, 60, t)},${lerp(26, 15, t)})`);
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, W, H);

      // heaven glow at top
      const glow = ctx.createRadialGradient(W / 2, -60, 10, W / 2, -60, 220);
      glow.addColorStop(0, `rgba(234,179,8,${0.15 + 0.55 * t})`);
      glow.addColorStop(1, "rgba(234,179,8,0)");
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, W, H);

      // road
      ctx.fillStyle = "rgba(15,17,26,0.9)";
      ctx.fillRect(16, 0, W - 32, H);
      ctx.strokeStyle = "rgba(234,179,8,0.4)";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(16, 0);
      ctx.lineTo(16, H);
      ctx.moveTo(W - 16, 0);
      ctx.lineTo(W - 16, H);
      ctx.stroke();

      // dashed lane markers
      ctx.strokeStyle = "rgba(255,255,255,0.3)";
      ctx.lineWidth = 2;
      ctx.setLineDash([16, 16]);
      for (let i = 1; i < LANES; i++) {
        const x = i * LANE_W;
        ctx.beginPath();
        ctx.moveTo(x, (roadOffset % 32) - 32);
        ctx.lineTo(x, H);
        ctx.stroke();
      }
      ctx.setLineDash([]);

      // ghost car — sample frames at accelerated time
      if (ghost) {
        const lane = laneAtTime(ghost.frames, elapsed / speedRef.current);
        const gx = lane * LANE_W + (LANE_W - CAR_W) / 2;
        const gy = H - CAR_H - 22;
        drawGhostCar(ctx, gx, gy);
      }

      // HUD label
      ctx.fillStyle = "rgba(234,179,8,0.8)";
      ctx.font = "500 10px 'JetBrains Mono', monospace";
      ctx.textAlign = "left";
      const pct = Math.round(t * 100);
      ctx.fillText(`REWIND · ${pct}%`, 20, 20);
    };

    const durationMs = () => {
      if (!ghost || !ghost.frames?.length) return 1;
      return ghost.frames[ghost.frames.length - 1].t;
    };

    const loop = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const elapsed = (ts - startRef.current) * speedRef.current;
      roadOffset += 5 * speedRef.current;
      draw(elapsed);
      if (ghost && elapsed >= durationMs() + 400) {
        setStatus("done");
        cancelAnimationFrame(rafRef.current);
        return;
      }
      rafRef.current = requestAnimationFrame(loop);
    };

    if (status === "playing") {
      startRef.current = 0;
      rafRef.current = requestAnimationFrame(loop);
    } else if (status === "ready" || status === "loading" || status === "error") {
      // paint a still frame so the canvas isn't blank
      draw(0);
    } else if (status === "done") {
      draw(durationMs());
    }
    return () => cancelAnimationFrame(rafRef.current);
  }, [status, ghost, open]);

  const startPlayback = () => {
    if (ghost) setStatus("playing");
  };
  const replay = () => {
    startRef.current = 0;
    setStatus("playing");
  };

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          data-testid="ghost-replay-overlay"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-black/85 backdrop-blur-md"
            onClick={onClose}
            aria-hidden="true"
          />
          <motion.div
            className="relative z-10 max-w-md w-full rts-glass rounded-2xl overflow-hidden"
            style={{
              boxShadow:
                "0 0 60px rgba(147,197,253,0.25), inset 0 0 0 1px rgba(147,197,253,0.25)",
            }}
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            data-testid="ghost-replay-modal"
          >
            <div className="p-6 md:p-8">
              <div className="flex items-center justify-between mb-6">
                <p className="font-mono-rts text-[0.68rem] tracking-[0.35em] uppercase text-blue-300/80">
                  Ghost Trail Rewind
                </p>
                <button
                  onClick={onClose}
                  className="w-8 h-8 rounded-full flex items-center justify-center text-white/50 hover:text-white hover:bg-white/5"
                  aria-label="Close"
                  data-testid="ghost-replay-close"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              <h3 className="font-serif-rts text-2xl md:text-3xl font-light text-white text-center">
                A fellow traveler&rsquo;s journey
              </h3>
              <p className="mt-2 text-center text-white/60 text-sm">
                Watch someone else&rsquo;s road home &mdash; anonymously.
              </p>

              <div className="mt-6 flex justify-center">
                <div
                  className="relative rounded-xl overflow-hidden border border-white/10"
                  style={{
                    boxShadow:
                      "0 0 40px rgba(147,197,253,0.12), inset 0 0 0 1px rgba(255,255,255,0.05)",
                  }}
                >
                  <canvas
                    ref={canvasRef}
                    width={W}
                    height={H}
                    className="block bg-[#05050A]"
                    data-testid="ghost-replay-canvas"
                  />
                  {status === "loading" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#05050A]/80">
                      <Loader2 className="w-6 h-6 text-[#EAB308] animate-spin" />
                    </div>
                  )}
                  {status === "error" && (
                    <div className="absolute inset-0 flex items-center justify-center bg-[#05050A]/90 text-center px-6">
                      <p className="text-white/70 text-sm">
                        No fellow travelers to rewind yet. Drive a run first &mdash;
                        yours may light the way.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                {status === "ready" && (
                  <button
                    onClick={startPlayback}
                    className="rts-btn-primary"
                    data-testid="ghost-replay-play-btn"
                  >
                    <Play className="w-4 h-4" /> Play the rewind
                  </button>
                )}
                {status === "playing" && (
                  <span className="text-xs uppercase tracking-[0.28em] text-blue-300/80 font-mono-rts">
                    Watching...
                  </span>
                )}
                {status === "done" && (
                  <button
                    onClick={replay}
                    className="rts-btn-primary"
                    data-testid="ghost-replay-again-btn"
                  >
                    <Play className="w-4 h-4" /> Watch again
                  </button>
                )}
              </div>

              {ghost && (
                <p className="mt-4 text-center font-mono-rts text-[0.62rem] tracking-[0.28em] uppercase text-white/40">
                  {ghost.distance.toLocaleString()} distance &middot;{" "}
                  {ghost.reached_heaven ? "reached heaven" : "still driving"}
                </p>
              )}
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

function lerp(a, b, t) {
  return Math.round(a + (b - a) * t);
}

function laneAtTime(frames, tMs) {
  if (!frames || frames.length === 0) return 1;
  if (tMs <= frames[0].t) return frames[0].lane;
  if (tMs >= frames[frames.length - 1].t) return frames[frames.length - 1].lane;
  for (let i = 0; i < frames.length - 1; i++) {
    if (frames[i].t <= tMs && frames[i + 1].t >= tMs) {
      return frames[i].lane;
    }
  }
  return frames[0].lane;
}

function drawGhostCar(ctx, x, y) {
  ctx.save();
  ctx.globalAlpha = 0.72;
  const glow = ctx.createRadialGradient(
    x + CAR_W / 2,
    y + CAR_H / 2,
    3,
    x + CAR_W / 2,
    y + CAR_H / 2,
    48,
  );
  glow.addColorStop(0, "rgba(147,197,253,0.7)");
  glow.addColorStop(1, "rgba(147,197,253,0)");
  ctx.fillStyle = glow;
  ctx.fillRect(x - 24, y - 24, CAR_W + 48, CAR_H + 48);
  const grad = ctx.createLinearGradient(x, y, x, y + CAR_H);
  grad.addColorStop(0, "#93C5FD");
  grad.addColorStop(1, "#3B82F6");
  ctx.fillStyle = grad;
  roundRect(ctx, x, y, CAR_W, CAR_H, 8);
  ctx.fillStyle = "rgba(255,255,255,0.15)";
  roundRect(ctx, x + 5, y + 12, CAR_W - 10, 24, 3);
  ctx.restore();
}

function roundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
}
